<template>
  <c-box
    :border-radius="{ md: 'base' }"
    background="white"
    :box-shadow="{ md: 'base' }"
    :mb="{ base: 'md', md: '3xl' }"
    width="100%"
    v-chakra="{
      '@media print': {
        borderTop: '1px solid',
        borderColor: 'gray.300',
        paddingTop: 'md',
      },
    }"
  >
    <c-heading
      as="h3"
      :px="{ base: 'md', sm: '2xl', lg: '5rem' }"
      :pt="{ base: 'md', sm: '3xl', lg: '3rem' }"
      :pb="{ base: 'md', sm: '0' }"
      font-size="1.6rem"
      font-weight="normal"
      v-chakra="{
        '@media print': {
          px: '0 !important',
          mb: 'sm',
        },
      }"
    >
      {{ $t("ordering.deliveryDetails.title") }}
    </c-heading>

    <c-flex
      :display="{ base: 'block', sm: 'flex' }"
      :px="{ base: 'md', sm: '2xl', lg: '5rem' }"
      :py="{ base: 'md', sm: '3xl', lg: '3rem' }"
      :mb="{ base: 'md', md: '0' }"
      v-chakra="{
        '@media print': {
          display: 'flex',
          px: '0',
          py: '0',
        },
      }"
      data-cy="order-delivery-details"
    >
      <c-flex :maxWidth="{ base: '100%', sm: '33.3333333%' }" width="100%" flexDirection="column">
        <!-- delivery address -->
        <c-heading as="h4" mb="sm" font-size="lg" font-weight="bold">
          {{ $t("ordering.deliveryDetails.deliveryAddress") }}
        </c-heading>
        <c-text color="gray.700">{{ order.deliveryDetails.companyName }}</c-text>
        <c-text color="gray.700">{{ order.deliveryDetails.receiverName }}</c-text>
        <c-text color="gray.700">{{ order.deliveryDetails.shipToAddress.streetAddress }}</c-text>
        <c-text v-if="order.deliveryDetails.shipToAddress.streetAddress2" color="gray.700">{{
          order.deliveryDetails.shipToAddress.streetAddress2
        }}</c-text>
        <c-text color="gray.700">
          {{ order.deliveryDetails.shipToAddress.zipCode }}
          {{ order.deliveryDetails.shipToAddress.city }}
        </c-text>
        <c-text v-if="order.deliveryDetails.shipToAddress.stateProvince" color="gray.700">
          {{ order.deliveryDetails.shipToAddress.stateProvince }}
        </c-text>
        <c-text v-else-if="order.deliveryDetails.shipToAddress.stateProvinceCode" color="gray.700">
          {{
            getCountrySubdivisionByCode(
              order.deliveryDetails.shipToAddress.countryCode,
              order.deliveryDetails.shipToAddress.stateProvinceCode,
            )
          }}
        </c-text>
        <c-text color="gray.700">
          {{ getCountryByCode(order.deliveryDetails.shipToAddress.countryCode) }}
        </c-text>

        <!-- contacts -->
        <c-heading
          as="h4"
          :mt="{ base: 'md', sm: '3xl' }"
          mb="sm"
          font-size="lg"
          font-weight="bold"
        >
          {{ $t("ordering.deliveryDetails.contacts") }}
        </c-heading>
        <c-text color="gray.700">{{ order.deliveryDetails.email }}</c-text>
        <c-text color="gray.700">{{ order.deliveryDetails.phoneNumber }}</c-text>
      </c-flex>

      <c-flex :maxWidth="{ base: '100%', sm: '33.3333333%' }" width="100%" flexDirection="column">
        <!-- customer reference -->
        <c-heading
          as="h4"
          :mt="{ base: 'md', sm: '0' }"
          mb="sm"
          font-size="lg"
          font-weight="bold"
          v-chakra="{ '@media print': { mt: '0' } }"
        >
          {{ $t("ordering.deliveryDetails.customerReference") }}
        </c-heading>
        <c-text v-if="order.deliveryDetails.reference" color="gray.700">
          {{ order.deliveryDetails.reference }}
        </c-text>
        <c-text v-else color="gray.700">-</c-text>

        <!-- external order number -->
        <c-heading
          as="h4"
          :mt="{ base: 'md', sm: '3xl' }"
          mb="sm"
          font-size="lg"
          font-weight="bold"
        >
          {{ $t("ordering.deliveryDetails.externalOrderNumber") }}
        </c-heading>
        <c-text v-if="order.deliveryDetails.externalOrderNumber" color="gray.700">
          {{ order.deliveryDetails.externalOrderNumber }}
        </c-text>
        <c-text v-else color="gray.700">-</c-text>

        <!-- additional details -->
        <c-heading
          as="h4"
          :mt="{ base: 'md', sm: '3xl' }"
          mb="sm"
          font-size="lg"
          font-weight="bold"
        >
          {{ $t("ordering.deliveryDetails.additionalDetails") }}
        </c-heading>
        <c-text v-if="order.deliveryDetails.additionalDetails" color="gray.700">
          {{ order.deliveryDetails.additionalDetails }}
        </c-text>
        <c-text v-else color="gray.700">-</c-text>

        <!-- project details -->
        <c-heading
          as="h4"
          :mt="{ base: 'md', sm: '3xl' }"
          mb="sm"
          font-size="lg"
          font-weight="bold"
        >
          {{ $t("ordering.projectDetails.title") }}
        </c-heading>
        <c-text color="gray.700">
          {{ $t("ordering.projectDetails.projectId") }}:
          <c-text v-if="order.discounts && order.discounts.projectDiscountCode" as="span">
            {{ order.discounts.projectDiscountCode }}
          </c-text>
          <c-text v-else as="span">-</c-text>
        </c-text>
        <c-text v-if="orderHasDiscounts()" color="gray.700">
          {{ $t("ordering.projectDetails.projectDiscountApplied") }}:
          <c-text v-if="order.discounts && order.discounts.percentagePointDiscount" as="span">
            {{ $t("ordering.projectDetails.projectDiscountApplied.percentagePointDiscount") }}
            -{{ order.discounts.percentagePointDiscount || 0 }}%
          </c-text>
          <c-text v-else-if="order.discounts && order.discounts.onlineDiscount" as="span">
            {{ $t("ordering.projectDetails.projectDiscountApplied.hardware") }} -{{
              order.discounts.discount || 0
            }}%, {{ $t("ordering.projectDetails.projectDiscountApplied.online") }} -{{
              order.discounts.onlineDiscount
            }}%<template v-if="(order.discounts.netPrices || []).length > 0"
              ><br />
              {{ $t("ordering.projectDetails.projectDiscountApplied.netPrices") }}
            </template>
          </c-text>
          <c-text v-else-if="order.discounts && order.discounts.discount" as="span">
            -{{ order.discounts.discount }}%<template
              v-if="(order.discounts.netPrices || []).length > 0"
              ><br />
              {{ $t("ordering.projectDetails.projectDiscountApplied.netPrices") }}
            </template>
          </c-text>
          <c-text
            v-else-if="order.discounts && (order.discounts.netPrices || []).length > 0"
            as="span"
          >
            {{ $t("ordering.projectDetails.projectDiscountApplied.netPrices") }}
          </c-text>
          <c-text v-else as="span">-</c-text>
        </c-text>
        <c-text v-if="orderHasDiscounts()" color="gray.700">
          {{ $t("ordering.projectDetails.projectOwner") }}:
          <c-text v-if="order.discounts && order.discounts.owner" as="span">
            {{ order.discounts.owner }}
          </c-text>
          <c-text v-else as="span">-</c-text>
        </c-text>
      </c-flex>

      <c-box :maxWidth="{ base: '100%', sm: '33.3333333%' }" width="100%">
        <!-- delivery method -->
        <c-heading
          as="h4"
          :mt="{ base: 'md', sm: '0' }"
          mb="sm"
          font-size="lg"
          font-weight="bold"
          v-chakra="{ '@media print': { mt: '0' } }"
        >
          {{ $t("ordering.deliveryDetails.deliveryMethod") }}
        </c-heading>
        <c-text color="gray.700">
          {{ getDeliveryTypeTextByCode(order.deliveryDetails.deliveryTypeCode) }}
        </c-text>

        <!-- requested shipping date -->
        <c-heading
          as="h4"
          :mt="{ base: 'md', sm: '3xl' }"
          mb="sm"
          font-size="lg"
          font-weight="bold"
        >
          {{ $t("ordering.deliveryDetails.requestedShippingDate") }}
        </c-heading>
        <c-text color="gray.700">
          {{
            getDateInILOQLocalTime(order.deliveryDetails.requestedDeliveryDate) | moment("D.M.YYYY")
          }}
        </c-text>

        <!-- shipping date -->
        <c-heading
          v-if="order.deliveryDetails.promisedDeliveryDate"
          as="h4"
          :mt="{ base: 'md', sm: '3xl' }"
          mb="sm"
          font-size="lg"
          font-weight="bold"
        >
          {{ $t("ordering.deliveryDetails.shippingDate") }}
        </c-heading>
        <c-text v-if="order.deliveryDetails.promisedDeliveryDate" color="gray.700">
          {{
            getDateInILOQLocalTime(order.deliveryDetails.promisedDeliveryDate) | moment("D.M.YYYY")
          }}
        </c-text>
      </c-box>
    </c-flex>

    <c-box
      :display="{ md: 'none' }"
      height="0.5rem"
      background="brand.grayLight"
      v-chakra="{
        '@media print': {
          display: 'none',
        },
      }"
    />

    <c-box
      :px="{ base: 'md', sm: '2xl', lg: '5rem' }"
      :py="{ base: 'md', sm: '3xl', lg: '3rem' }"
      v-chakra="{
        '@media print': {
          px: '0',
          py: '0',
          borderTop: '1px solid',
          borderColor: 'gray.300',
        },
      }"
    >
      <c-box data-cy="order-products">
        <table
          v-chakra="{
            mb: 'xs',
            width: '100%',
          }"
        >
          <thead
            v-chakra="{
              display: { base: 'none', md: 'table-header-group' },
              '@media print': {
                display: 'table-header-group',
                height: '4rem',
              },
            }"
          >
            <tr
              v-chakra="
                (theme) => ({
                  borderBottomWidth: theme.border.base,
                  borderColor: 'gray.300',
                  textAlign: 'left',
                })
              "
            >
              <th v-chakra pb="xs" pr="sm">{{ $t("shoppingCart.heading.quantity") }}</th>
              <th v-chakra pb="xs" pr="sm">{{ $t("shoppingCart.heading.product") }}</th>
              <th v-chakra pb="xs" pr="sm">{{ $t("shoppingCart.heading.productId") }}</th>
              <th v-if="!order.orderStatus" v-chakra pb="xs" pr="sm">
                {{ $t("shoppingCart.heading.availability") }}
              </th>
              <th v-chakra pb="xs" pr="sm">{{ $t("shoppingCart.heading.unitPrice") }}</th>
              <th v-chakra pb="xs" text-align="right" white-space="nowrap">
                {{ $t("shoppingCart.heading.totalPrice") }}
              </th>
            </tr>
          </thead>

          <tr
            v-for="cartItem in withoutServiceProductsAndExtraFees"
            :key="cartItem.product.id"
            v-bind:class="{ checking_availability: checkingAvailability }"
            v-chakra="
              (theme) => ({
                borderBottomWidth: theme.border.base,
                borderColor: 'gray.300',
                textAlign: 'left',
                pb: { base: 'sm', md: '0' },
                mb: { base: 'sm', md: '0' },
                display: { base: 'block', md: 'table-row' },
                '@media print': {
                  display: 'table-row',
                  height: '4rem',
                },
                '@media print > td': {
                  display: 'table-cell',
                },
              })
            "
          >
            <!-- quantity -->
            <td
              v-chakra="{
                py: { base: '0.15rem', md: 'lg' },
                pr: { md: 'sm' },
                display: { base: 'flex', md: 'table-cell' },
                flexDirection: 'row',
                alignContent: 'center',
                justifyContent: 'space-between',
                '@media print': {
                  display: 'table-cell',
                },
              }"
            >
              <c-text
                as="span"
                :display="{ base: 'block', md: 'none' }"
                mr="sm"
                fontWeight="bold"
                v-chakra="{
                  '@media print': {
                    display: 'none !important',
                  },
                }"
              >
                {{ $t("shoppingCart.heading.quantity") }}
              </c-text>
              {{ cartItem.quantity }}
            </td>

            <!-- product name -->
            <td
              v-chakra="{
                py: { base: '0.15rem', md: 'lg' },
                pr: { md: 'sm' },
                display: { base: 'flex', md: 'table-cell' },
                flexDirection: 'row',
                alignContent: 'center',
                justifyContent: 'space-between',
                '@media print': {
                  display: 'table-cell',
                },
              }"
            >
              <c-text
                as="span"
                :display="{ base: 'block', md: 'none' }"
                mr="sm"
                fontWeight="bold"
                v-chakra="{
                  '@media print': {
                    display: 'none !important',
                  },
                }"
              >
                {{ $t("shoppingCart.heading.product") }}
              </c-text>
              <c-box display="grid" :width="{ md: '100%' }">
                <c-text
                  as="span"
                  text-overflow="ellipsis"
                  overflow="hidden"
                  display="inline-block"
                  width="100%"
                  white-space="nowrap"
                  :title="cartItem.product.name"
                >
                  {{ cartItem.product.name }}
                </c-text>
                <c-text as="span" v-if="cartItem.product.additional_note" class="additional_note">
                  {{ cartItem.product.additional_note }}
                </c-text>
              </c-box>
            </td>

            <!-- product id -->
            <td
              v-chakra="{
                py: { base: '0.15rem', md: 'lg' },
                pr: { md: 'sm' },
                display: { base: 'flex', md: 'table-cell' },
                flexDirection: 'row',
                alignContent: 'center',
                justifyContent: 'space-between',
                '@media print': {
                  display: 'table-cell',
                },
              }"
            >
              <c-text
                as="span"
                :display="{ base: 'block', md: 'none' }"
                mr="sm"
                fontWeight="bold"
                v-chakra="{
                  '@media print': {
                    display: 'none !important',
                  },
                }"
              >
                {{ $t("shoppingCart.heading.productId") }}
              </c-text>
              {{ cartItem.product.id }}
            </td>

            <!-- availability -->
            <td
              v-if="!order.orderStatus"
              v-chakra="{
                py: { base: '0.15rem', md: 'lg' },
                pr: { md: 'sm' },
                display: { base: 'flex', md: 'table-cell' },
                flexDirection: 'row',
                alignContent: 'center',
                justifyContent: 'space-between',
                '@media print': {
                  display: 'table-cell',
                },
              }"
            >
              <c-text
                as="span"
                :display="{ base: 'block', md: 'none' }"
                mr="sm"
                fontWeight="bold"
                v-chakra="{
                  '@media print': {
                    display: 'none !important',
                  },
                }"
              >
                {{ $t("shoppingCart.heading.availability") }}
              </c-text>
              <availability-info
                :availability="cartItem.availability"
                :availability-date="cartItem.availabilityDate"
              />
            </td>

            <!-- price -->
            <td
              v-chakra="{
                py: { base: '0.15rem', md: 'lg' },
                pr: { md: 'sm' },
                display: { base: 'flex', md: 'table-cell' },
                flexDirection: 'row',
                alignContent: 'center',
                justifyContent: 'space-between',
                '@media print': {
                  display: 'table-cell',
                },
              }"
            >
              <c-text
                as="span"
                :display="{ base: 'block', md: 'none' }"
                mr="sm"
                fontWeight="bold"
                v-chakra="{
                  '@media print': {
                    display: 'none !important',
                  },
                }"
              >
                {{ $t("shoppingCart.heading.unitPrice") }}
              </c-text>
              <Price
                v-chakra
                :price="cartItem.product.price?.price ?? 0"
                :campaign-price="campaignPrice(cartItem, order.discounts)"
                :currency="cartItem.product.price?.currencyCode"
              />
            </td>

            <!-- total price -->
            <td
              v-chakra="{
                py: { base: '0.15rem', md: 'lg' },
                pr: { md: 'sm' },
                display: { base: 'flex', md: 'table-cell' },
                flexDirection: 'row',
                alignContent: 'center',
                justifyContent: 'space-between',
                textAlign: 'right',
                '@media print': {
                  display: 'table-cell',
                },
              }"
            >
              <c-text
                as="span"
                :display="{ base: 'block', md: 'none' }"
                mr="sm"
                text-align="left"
                fontWeight="bold"
                v-chakra="{
                  '@media print': {
                    display: 'none !important',
                  },
                }"
              >
                {{ $t("shoppingCart.heading.totalPrice") }}
              </c-text>
              <Price
                :price="cartItem.rowTotalSum"
                :currency="cartItem.product.price?.currencyCode"
                align="right"
              />
            </td>
          </tr>

          <tr
            v-if="serviceProducts.length"
            v-chakra="
              (theme) => ({
                borderBottomWidth: theme.border.base,
                borderColor: theme.colors.gray[300],
                pb: { base: 'sm', md: '0' },
                mb: { base: 'sm', md: '0' },
                display: { base: 'block', md: 'table-row' },
                '@media print': {
                  display: 'table-row',
                  height: '4rem',
                },
              })
            "
          >
            <td
              v-chakra="{
                py: { base: '0.15rem', md: 'lg' },
                display: { base: 'flex', md: 'table-cell' },
                textAlign: 'left',
                '@media print': {
                  display: 'table-cell',
                },
              }"
            >
              <c-text
                :mt="{ base: 0, md: '0' }"
                :mb="{ base: 'sm', md: '0' }"
                :font-size="{ base: 'lg', md: 'sm' }"
                font-weight="bold"
                v-chakra="{
                  '@media print': {
                    fontSize: 'sm',
                    my: '0',
                  },
                }"
              >
                {{ $t("ordering.serviceProducts.title") }}
              </c-text>
            </td>
          </tr>

          <tr
            v-for="cartItem in serviceProducts"
            :key="cartItem.product.id"
            v-chakra="
              (theme) => ({
                borderBottomWidth: theme.border.base,
                borderColor: theme.colors.gray[300],
                pb: { base: 'sm', md: '0' },
                mb: { base: 'sm', md: '0' },
                display: { base: 'block', md: 'table-row' },
                '@media print': {
                  display: 'table-row',
                  height: '4rem',
                },
              })
            "
          >
            <!-- spacer -->
            <td
              v-chakra="{
                py: { base: '0.15rem', md: 'lg' },
                pr: { md: 'sm' },
                display: { base: 'flex', md: 'table-cell' },
                flexDirection: 'row',
                alignContent: 'center',
                justifyContent: 'space-between',
                '@media print': {
                  display: 'table-cell',
                },
              }"
            ></td>

            <!-- with name and subtitle -->
            <td
              v-if="cartItem.product.name && cartItem.product.subtitle"
              v-chakra="{
                py: { base: '0.15rem', md: 'lg' },
                pr: { md: 'sm' },
                display: { base: 'flex', md: 'table-cell' },
                flexDirection: 'row',
                alignContent: 'center',
                justifyContent: 'space-between',
                '@media print': {
                  display: 'table-cell',
                },
              }"
            >
              {{ cartItem.product.name }}, {{ cartItem.product.subtitle }}
            </td>
            <!-- with name -->
            <td
              v-else
              v-chakra="{
                py: { base: '0.15rem', md: 'lg' },
                pr: { md: 'sm' },
                display: { base: 'flex', md: 'table-cell' },
                flexDirection: 'row',
                alignContent: 'center',
                justifyContent: 'space-between',
                '@media print': {
                  display: 'table-cell',
                },
              }"
            >
              {{ cartItem.product.name }}
            </td>

            <!-- spacer -->
            <td
              v-chakra="{
                py: { base: '0.15rem', md: 'lg' },
                pr: { md: 'sm' },
                display: { base: 'flex', md: 'table-cell' },
                flexDirection: 'row',
                alignContent: 'center',
                justifyContent: 'space-between',
                '@media print': {
                  display: 'table-cell',
                },
              }"
            ></td>

            <!-- spacer -->
            <td
              v-if="!order.orderStatus"
              v-chakra="{
                py: { base: '0.15rem', md: 'lg' },
                pr: { md: 'sm' },
                display: { base: 'flex', md: 'table-cell' },
                flexDirection: 'row',
                alignContent: 'center',
                justifyContent: 'space-between',
                '@media print': {
                  display: 'table-cell',
                },
              }"
            ></td>

            <td
              v-chakra="{
                py: { base: '0.15rem', md: 'lg' },
                pr: { md: 'sm' },
                display: { base: 'flex', md: 'table-cell' },
                flexDirection: 'row',
                alignContent: 'center',
                justifyContent: 'space-between',
                '@media print': {
                  display: 'table-cell',
                },
              }"
            >
              <c-text
                as="span"
                :display="{ base: 'block', md: 'none' }"
                mr="sm"
                text-align="left"
                v-chakra="{
                  '@media print': {
                    display: 'none !important',
                  },
                }"
              >
                {{ $t("shoppingCart.heading.unitPrice") }}
              </c-text>
              <c-text as="span" :font-weight="{ base: 'bold', md: 'normal' }">
                {{
                  cartItem.product.price_factor && round(cartItem.product.price_factor * 100, 2)
                }}%
              </c-text>
            </td>
            <td
              v-chakra="{
                py: { base: '0.15rem', md: 'lg' },
                pr: { md: 'sm' },
                display: { base: 'flex', md: 'table-cell' },
                flexDirection: 'row',
                alignContent: 'center',
                justifyContent: 'space-between',
                '@media print': {
                  display: 'table-cell',
                },
              }"
            >
              <c-text
                as="span"
                :display="{ base: 'block', md: 'none' }"
                mr="sm"
                text-align="left"
                v-chakra="{
                  '@media print': {
                    display: 'none !important',
                  },
                }"
              >
                {{ $t("shoppingCart.heading.totalPrice") }}
              </c-text>
              <c-text as="span">
                <Price
                  v-if="!order.orderNumber"
                  :currency="cartItem.product.price.currencyCode"
                  :price="order.totalSum.price * cartItem.product.price_factor"
                  align="right"
                />
                <Price
                  v-else
                  :currency="cartItem.product.price.currencyCode"
                  :price="cartItem.product.price.price"
                  align="right"
                />
              </c-text>
            </td>
          </tr>

          <tr
            v-if="orderHandlingFee || orderSurfaceTreatmentFee"
            v-chakra="
              (theme) => ({
                borderBottomWidth: theme.border.base,
                borderColor: theme.colors.gray[300],
                pb: { base: 'sm', md: '0' },
                mb: { base: 'sm', md: '0' },
                display: { base: 'block', md: 'table-row' },
                '@media print': {
                  display: 'table-row',
                  height: '4rem',
                },
              })
            "
          >
            <td
              v-chakra="{
                py: { base: '0.15rem', md: 'lg' },
                display: { base: 'flex', md: 'table-cell' },
                textAlign: 'left',
                '@media print': {
                  display: 'table-cell',
                },
              }"
            >
              <c-text
                :mb="{ base: 'sm', md: '0' }"
                :font-size="{ base: 'lg', md: 'sm' }"
                font-weight="bold"
                v-chakra="{
                  '@media print': {
                    fontSize: 'sm',
                    mb: '0',
                  },
                }"
              >
                {{ $t("ordering.handlingFees.title") }}
              </c-text>
            </td>
          </tr>

          <tr
            v-if="orderHandlingFee"
            v-chakra="
              (theme) => ({
                borderBottomWidth: theme.border.base,
                borderColor: theme.colors.gray[300],
                pb: { base: 'sm', md: '0' },
                mb: { base: 'sm', md: '0' },
                display: { base: 'block', md: 'table-row' },
                '@media print': {
                  display: 'table-row',
                  height: '4rem',
                },
              })
            "
            data-cy="handling-fee-row"
          >
            <!-- spacer -->
            <td
              v-chakra="{
                py: { base: '0.15rem', md: 'lg' },
                pr: { md: 'sm' },
                display: { base: 'flex', md: 'table-cell' },
                flexDirection: 'row',
                alignContent: 'center',
                justifyContent: 'space-between',
                '@media print': {
                  display: 'table-cell',
                },
              }"
            ></td>

            <td
              v-chakra="{
                py: { base: '0.15rem', md: 'lg' },
                pr: { md: 'sm' },
                display: { base: 'flex', md: 'table-cell' },
                flexDirection: 'row',
                alignContent: 'center',
                justifyContent: 'space-between',
                '@media print': {
                  display: 'table-cell',
                },
              }"
            >
              {{ orderHandlingFee.name }}
            </td>

            <!-- spacer -->
            <td
              v-chakra="{
                py: { base: '0.15rem', md: 'lg' },
                pr: { md: 'sm' },
                display: { base: 'flex', md: 'table-cell' },
                flexDirection: 'row',
                alignContent: 'center',
                justifyContent: 'space-between',
                '@media print': {
                  display: 'table-cell',
                },
              }"
            ></td>

            <!-- spacer -->
            <td
              v-if="!order.orderStatus"
              v-chakra="{
                py: { base: '0.15rem', md: 'lg' },
                pr: { md: 'sm' },
                display: { base: 'flex', md: 'table-cell' },
                flexDirection: 'row',
                alignContent: 'center',
                justifyContent: 'space-between',
                '@media print': {
                  display: 'table-cell',
                },
              }"
            ></td>

            <td
              v-chakra="{
                py: { base: '0.15rem', md: 'lg' },
                pr: { md: 'sm' },
                display: { base: 'flex', md: 'table-cell' },
                flexDirection: 'row',
                alignContent: 'center',
                justifyContent: 'space-between',
                '@media print': {
                  display: 'table-cell',
                },
              }"
            >
              <c-text
                as="span"
                :display="{ base: 'block', md: 'none' }"
                mr="sm"
                text-align="left"
                v-chakra="{
                  '@media print': {
                    display: 'none !important',
                  },
                }"
              >
                {{ $t("shoppingCart.heading.unitPrice") }}
              </c-text>
              <c-text as="span" :font-weight="{ base: 'bold', md: 'normal' }">
                <Price
                  v-chakra
                  :price="orderHandlingFee.price?.price ?? 0"
                  :currency="orderHandlingFee.price?.currencyCode"
                />
              </c-text>
            </td>
            <td
              v-chakra="{
                py: { base: '0.15rem', md: 'lg' },
                pr: { md: 'sm' },
                display: { base: 'flex', md: 'table-cell' },
                flexDirection: 'row',
                alignContent: 'center',
                justifyContent: 'space-between',
                '@media print': {
                  display: 'table-cell',
                },
              }"
            >
              <c-text
                as="span"
                :display="{ base: 'block', md: 'none' }"
                mr="sm"
                text-align="left"
                v-chakra="{
                  '@media print': {
                    display: 'none !important',
                  },
                }"
              >
                {{ $t("shoppingCart.heading.totalPrice") }}
              </c-text>
              <c-text as="span">
                <Price
                  :currency="orderHandlingFee.price?.currencyCode"
                  :price="orderHandlingFee.price?.price ?? 0"
                  align="right"
                />
              </c-text>
            </td>
          </tr>

          <tr
            v-if="orderSurfaceTreatmentFee"
            v-chakra="
              (theme) => ({
                borderBottomWidth: theme.border.base,
                borderColor: theme.colors.gray[300],
                pb: { base: 'sm', md: '0' },
                mb: { base: 'sm', md: '0' },
                display: { base: 'block', md: 'table-row' },
                '@media print': {
                  display: 'table-row',
                  height: '4rem',
                },
              })
            "
            data-cy="surface-treatment-fee-row"
          >
            <!-- spacer -->
            <td
              v-chakra="{
                py: { base: '0.15rem', md: 'lg' },
                pr: { md: 'sm' },
                display: { base: 'flex', md: 'table-cell' },
                flexDirection: 'row',
                alignContent: 'center',
                justifyContent: 'space-between',
                '@media print': {
                  display: 'table-cell',
                },
              }"
            ></td>

            <td
              v-chakra="{
                py: { base: '0.15rem', md: 'lg' },
                pr: { md: 'sm' },
                display: { base: 'flex', md: 'table-cell' },
                flexDirection: 'row',
                alignContent: 'center',
                justifyContent: 'space-between',
                '@media print': {
                  display: 'table-cell',
                },
              }"
            >
              {{ orderSurfaceTreatmentFee.name }}
            </td>

            <!-- spacer -->
            <td
              v-chakra="{
                py: { base: '0.15rem', md: 'lg' },
                pr: { md: 'sm' },
                display: { base: 'flex', md: 'table-cell' },
                flexDirection: 'row',
                alignContent: 'center',
                justifyContent: 'space-between',
                '@media print': {
                  display: 'table-cell',
                },
              }"
            ></td>

            <!-- spacer -->
            <td
              v-if="!order.orderStatus"
              v-chakra="{
                py: { base: '0.15rem', md: 'lg' },
                pr: { md: 'sm' },
                display: { base: 'flex', md: 'table-cell' },
                flexDirection: 'row',
                alignContent: 'center',
                justifyContent: 'space-between',
                '@media print': {
                  display: 'table-cell',
                },
              }"
            ></td>

            <td
              v-chakra="{
                py: { base: '0.15rem', md: 'lg' },
                pr: { md: 'sm' },
                display: { base: 'flex', md: 'table-cell' },
                flexDirection: 'row',
                alignContent: 'center',
                justifyContent: 'space-between',
                '@media print': {
                  display: 'table-cell',
                },
              }"
            >
              <c-text
                as="span"
                :display="{ base: 'block', md: 'none' }"
                mr="sm"
                text-align="left"
                v-chakra="{
                  '@media print': {
                    display: 'none !important',
                  },
                }"
              >
                {{ $t("shoppingCart.heading.unitPrice") }}
              </c-text>
              <c-text as="span" :font-weight="{ base: 'bold', md: 'normal' }">
                <Price
                  v-chakra
                  :price="orderSurfaceTreatmentFee.price?.price ?? 0"
                  :currency="orderSurfaceTreatmentFee.price?.currencyCode"
                />
              </c-text>
            </td>
            <td
              v-chakra="{
                py: { base: '0.15rem', md: 'lg' },
                pr: { md: 'sm' },
                display: { base: 'flex', md: 'table-cell' },
                flexDirection: 'row',
                alignContent: 'center',
                justifyContent: 'space-between',
                '@media print': {
                  display: 'table-cell',
                },
              }"
            >
              <c-text
                as="span"
                :display="{ base: 'block', md: 'none' }"
                mr="sm"
                text-align="left"
                v-chakra="{
                  '@media print': {
                    display: 'none !important',
                  },
                }"
              >
                {{ $t("shoppingCart.heading.totalPrice") }}
              </c-text>
              <c-text as="span">
                <Price
                  :currency="orderSurfaceTreatmentFee.price?.currencyCode"
                  :price="orderSurfaceTreatmentFee.price?.price ?? 0"
                  align="right"
                />
              </c-text>
            </td>
          </tr>
        </table>

        <c-flex flexDirection="column" alignItems="end">
          <c-text as="span" fontWeight="bold">{{ $t("shoppingCart.heading.totalPrice") }}</c-text>
          <ShoppingCartTotalPrice :order="order" />
        </c-flex>
      </c-box>
    </c-box>
  </c-box>
</template>

<script lang="ts">
import Vue, { defineComponent } from "vue";
import {
  IAvailabilityRequest,
  IOrderRow,
  IProductCategory,
  countryList,
  countrySubdivisionList,
  HandlingFee,
  IProductData,
  IProduct,
  IPartnerInfo,
  SurfaceTreatmentFee,
} from "@iloq/shared";
import Price from "@/design-system/elements/Price.vue";
import deliveryTypes from "@/components/DeliveryTypes";
import { isBundledProduct, isSurfaceTreatmentFeeRequired } from "../util/ProductUtil";
import { mapState } from "vuex";
import moment from "moment";
import { round } from "lodash";
import ShoppingCartTotalPrice from "./ShoppingCartTotalPrice.vue";
import { Availability } from "../store/shoppingCartModule";
import { CBox, CHeading, CText, CFlex } from "@chakra-ui/vue";
import AvailabilityInfo from "@/components/ordering/AvailabilityInfo.vue";
import { orderMixin } from "./ordering/OrderMixins";

export default defineComponent({
  name: "ViewOrderDetailsBox",
  components: {
    Price,
    ShoppingCartTotalPrice,
    CBox,
    CFlex,
    CHeading,
    CText,
    AvailabilityInfo,
  },
  mixins: [orderMixin],
  data() {
    return {
      checkingAvailability: false,
      countryList: countryList,
      partnerDeliveryTypes: null as any,
    };
  },
  props: {
    order: {
      type: Object,
      required: true,
    },
  },
  computed: {
    ...mapState("partnerInfo", {
      partnerInfo: "partnerInfo",
    }),
    ...mapState("productSearch", {
      categoryTree: "categoryTree",
    }),
    ...mapState("productData", {
      handlingFee: "handlingFee",
      surfaceTreatmentFee: "surfaceTreatmentFee",
    }),
    serviceProducts(): any[] {
      return (
        this.order?.rows?.filter((row: IOrderRow) => this.isServiceproductId(row.product?.id)) || []
      );
    },
    withoutServiceProductsAndExtraFees(): any[] {
      return (
        this.order?.rows?.filter(
          (row: IOrderRow) =>
            !this.isServiceproductId(row.product?.id) &&
            row.product?.id !== HandlingFee.sku &&
            row.product?.id !== SurfaceTreatmentFee.sku,
        ) || []
      );
    },
    orderHandlingFee(): IProduct | undefined {
      return this.getExtraFee(
        HandlingFee.sku,
        this.handlingFee,
        !this.partnerInfo?.hasFreeHandling,
      );
    },
    orderSurfaceTreatmentFee(): IProduct | undefined {
      return this.getExtraFee(
        SurfaceTreatmentFee.sku,
        this.surfaceTreatmentFee,
        isSurfaceTreatmentFeeRequired(this.order.rows),
      );
    },
  },
  methods: {
    checkAvailability(): void {
      if (!this.checkingAvailability) {
        this.checkingAvailability = true;
      }
      const cartItems = this.$store.state.shoppingCart.cartRows;
      const availabilityRequest: IAvailabilityRequest = { items: [] };
      const serviceproductIds = this.$store.state.productData.serviceproducts.ids;

      cartItems.forEach((cartItem: IOrderRow) => {
        if (
          !serviceproductIds.includes(cartItem.product.id) &&
          !isBundledProduct(cartItem, this.categoryTree)
        ) {
          const productID: string = cartItem.product.id;
          const productQuantity: number = cartItem.quantity;
          const requestDate: any =
            this.$store.state.shoppingCart.deliveryDetails.requestedDeliveryDate.toISOString();
          availabilityRequest.items.push({
            productId: productID,
            requestedDeliveryDate: requestDate,
            quantity: productQuantity,
          });
        } else {
          cartItem.availability = Availability.NOT_AVAILABLE;
        }
      });
      if (availabilityRequest.items.length) {
        this.$store
          .dispatch("productAvailability/getAvailabilities", availabilityRequest)
          .then((results: Array<string>) => {
            this.setAvailabilities(results);
            this.checkingAvailability = false;
          })
          .catch(() => {
            this.$store.commit("showGeneralError", {
              dialogTitle: this.$t("ordering.errorNotification.header"),
              dialogText: this.$t("ordering.errorNotification.availabilityTakingLong"),
            });
            this.checkingAvailability = false;
          });
      } else {
        this.checkingAvailability = false;
      }
    },
    setAvailabilities(results: Array<string>): void {
      this.$store.dispatch("shoppingCart/setAvailabilities", results);
    },
    getCountryByCode(code: string): string | undefined {
      return countryList().find((item: any) => item.v === code)?.t;
    },
    getCountrySubdivisionByCode(countryCode: string, subdivisionCode: string): string | undefined {
      return countrySubdivisionList(countryCode)!.find((item: any) => item.v === subdivisionCode)
        ?.t;
    },
    getDeliveryTypeTextByCode(code: string): string {
      let result = code; // fall back to plain code if no text for it can be found
      const commonType = deliveryTypes().all.find((item: any) => item.v === code);
      if (commonType) {
        result = this.$t(commonType.t) as string;
      } else {
        const partnerType = this.partnerDeliveryTypes?.find((item: any) => item.code === code);
        if (partnerType) {
          result = partnerType.name;
        }
      }
      return result;
    },
    getDateInILOQLocalTime(date?: Date): any {
      return date ? moment(date).utc(false).tz("Europe/Helsinki") : undefined;
    },
    getExtraFee(
      feeSku: string,
      feeProduct: IProductData | null,
      feeRequired: boolean,
    ): IProduct | undefined {
      if (this.order.orderNumber) {
        const existingExtraFee = (this.order.rows as IOrderRow[]).find(
          (row) => row.product?.id === feeSku,
        );

        const { product } = existingExtraFee || {};
        return product;
      }

      if (feeProduct === null || !feeRequired) {
        return undefined;
      }

      const product: IProduct = {
        id: feeProduct.sku,
        price: {
          currencyCode: feeProduct.currencyCode,
          price: feeProduct.price,
        },
        name: feeProduct.product_name,
        categories: feeProduct.categories ?? [],
        imageUrl: feeProduct.main_image,
        family: feeProduct.family,
        additional_note: feeProduct.additional_note,
        main_category: feeProduct.main_category,
        antimicrobial_coating: feeProduct.antimicrobial_coating,
      };
      return product;
    },
    isServiceproductId(id: string): boolean {
      return this.$store.state.productData.serviceproducts.ids.includes(id);
    },
    round(number: number, precision?: number) {
      return round(number, precision ?? 0);
    },
    orderHasDiscounts(): boolean {
      return (
        (this.order.discounts.discount ?? 0) > 0 ||
        (this.order.discounts.onlineDiscount ?? 0) > 0 ||
        (this.order.discounts.netPrices?.length ?? 0) > 0 ||
        (this.order.discounts.percentagePointDiscount ?? 0) > 0 ||
        !!this.order.discounts.discountsRequireManualProcessing
      );
    },
  },
  mounted() {
    if (!this.order.orderStatus) {
      this.checkAvailability();
    }
  },
});
</script>
